import {useMap} from "react-leaflet";
import {useCallback, useEffect, useRef} from "react";
import {leafletRasterLayer, PMTiles} from "pmtiles";
import {useLeafletContext} from "@react-leaflet/core";
import L from "leaflet";

const PMTilesLayer = ({id, url, setLoaded, isVisible = true}) => {
    const map = useMap();
    const layerRef = useRef(null);
    const context = useLeafletContext();
    const loading = useRef(false);

    const showHideAndFlyTo = useCallback((isVisible, id) => {
        const container = context.layerContainer || context.map;
        if (layerRef.current?.layer) {
            if (isVisible) {
                // console.log("[PMTL] showing ", id)
                container.addLayer(layerRef.current.layer);
                const {lat, lng} = layerRef.current.latLng;
                if (lat && lng) {
                    // console.log("flying to", id);
                    const currentZoom = container.getZoom();
                    context.map.flyTo(new L.LatLng(lat, lng),
                        currentZoom,
                        {animate: false});
                }
            } else {
                // console.log("[GTL] hiding ", id)
                container.removeLayer(layerRef.current.layer)
            }
        }
    }, [context]);

    useEffect(() => {
        if (!map || !url || loading.current || layerRef.current?.layer) return;
        loading.current = true;

        const p = new PMTiles(url);
        p.getHeader().then(({minLat, minLon, maxLat, maxLon}) => {
                const center = findCenterOfBounds([{lat: minLat, lng: minLon}, {lat: maxLat, lng: maxLon}])
                // console.log("pmtiles center", center)
                layerRef.current = {layer: leafletRasterLayer(p, {}), latLng: {lat: center.lat, lng: center.lng}};
                layerRef.current.layer.addTo(map);
                showHideAndFlyTo(isVisible, id);
                setLoaded(true);
                loading.current = false;
            }
        ).catch(err => {
            console.error("Error loading floor plan", err);
        });
    }, [id, isVisible, map, setLoaded, showHideAndFlyTo, url]);

    // hide/show layer when a different floor is selected
    useEffect(() => {
        showHideAndFlyTo(isVisible, id);
    }, [showHideAndFlyTo, isVisible, id]);

    // clean up when un-mounted
    useEffect(() => {
        const container = context.layerContainer || context.map;
        return () => {
            if (layerRef.current?.layer) {
                container.removeLayer(layerRef.current.layer)
                layerRef.current = {latLng: null, layer: null};
                loading.current = false;
            }
        }
    }, [context]);

    return null;
};

export default PMTilesLayer;

function findCenterOfBounds(coordinates) {
    // Ensure the input is a valid array with at least one coordinate
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
        console.error("Input must be a non-empty array of coordinates.");
        return null;
    }

    let totalLat = 0;
    let totalLng = 0;

    // Sum up all latitudes and longitudes
    for (const coord of coordinates) {
        // Validate each coordinate object
        if (typeof coord.lat !== 'number' || typeof coord.lng !== 'number') {
            console.error("Each coordinate must have a valid 'lat' and 'lng' property.");
            return null;
        }
        totalLat += coord.lat;
        totalLng += coord.lng;
    }

    // Calculate the average
    const centerLat = totalLat / coordinates.length;
    const centerLng = totalLng / coordinates.length;

    return {lat: centerLat, lng: centerLng};
}
